import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./TOC.css";

/**
 * TOC (Table of Contents) — tracks visible headings via IntersectionObserver.
 *
 * Keyboard accessible: all links are standard anchor elements.
 * Smooth scroll is opt-in (respects prefers-reduced-motion).
 */
export default function TOC({ items }) {
  const { t } = useTranslation("blog");
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");

  useEffect(() => {
    const ids      = items.map(({ id }) => id);
    const elements = ids.map((id) => document.getElementById(id)).filter(Boolean);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((e) => e.isIntersecting);
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const handleClick = useCallback(
    (e, id) => {
      e.preventDefault();
      document.getElementById(id)?.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
      // Update URL hash without triggering a navigation
      window.history.replaceState(null, "", `#${id}`);
    },
    [prefersReducedMotion]
  );

  return (
    <nav className="toc" aria-label="Table of contents">
      <p className="toc-label" aria-hidden="true">{t("toc")}</p>
      <ol className="toc-list">
        {items.map(({ id, title }) => (
          <li key={id}>
            <a
              href={`#${id}`}
              className={`toc-link${activeId === id ? " toc-link--active" : ""}`}
              onClick={(e) => handleClick(e, id)}
              aria-current={activeId === id ? "true" : undefined}
            >
              {title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

TOC.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id:    PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
};
